import type { Dispatch, SetStateAction } from 'react';

import type { ApolloLink } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import type { Apollo } from 'src/gql/generated';
import type {
  LookupDocxUploadDocument,
  UploadAsLatestVersionChoice,
} from 'src/helpers/docx-upload-target';
import { resolveDocxUploadTarget } from 'src/helpers/docx-upload-target';
import { extractFileName } from 'src/helpers/integrations/filestack';
import type { FilestackUploadPayload } from 'src/helpers/integrations/filestack-upload-payload';
import { Sentry } from 'src/sentry';

import { REPOSITORY_DROPZONE_SENTRY_FEATURE } from '../../RepositoryDropzoneConfig';
import type { UploadFile } from '../../useRepositoryDropzone';

type RepositoryDropzoneCreateFiles = (
  options: Parameters<Apollo.CreateFilesMutationHookResult[0]>[0] & { reportId?: UUID }
) => Promise<ApolloLink.Result<Apollo.CreateFilesMutation>>;

type RepositoryDropzoneCreateStaticFileVersions = (
  options: Parameters<Apollo.CreateStaticFileVersionsMutationHookResult[0]>[0]
) => Promise<ApolloLink.Result<Apollo.CreateStaticFileVersionsMutation>>;

export interface RepositoryDropzoneSuccessfulStackEntry {
  id: string;
  result: FilestackUploadPayload;
}

interface PersistRepositoryDropzoneUploadsParams {
  confirmUploadAsLatestVersion: (documentName: string) => Promise<UploadAsLatestVersionChoice>;
  createFiles: RepositoryDropzoneCreateFiles;
  createStaticFileVersions: RepositoryDropzoneCreateStaticFileVersions;
  documentVariant: Apollo.DocumentVariant;
  fileRefs: Map<string, File>;
  isSigned: boolean;
  lookupDocument: LookupDocxUploadDocument;
  repositoryId: UUID;
  setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>;
  successfulOrdered: RepositoryDropzoneSuccessfulStackEntry[];
}

interface VersionPersistEntry {
  documentId: UUID;
  entry: RepositoryDropzoneSuccessfulStackEntry;
}

/**
 * Persists successfully uploaded files to the database via GraphQL mutation.
 * Updates upload file states to 'completed' or 'failed' based on mutation result.
 */
export async function persistRepositoryDropzoneUploads(
  params: PersistRepositoryDropzoneUploadsParams
): Promise<void> {
  const {
    confirmUploadAsLatestVersion,
    createFiles,
    createStaticFileVersions,
    documentVariant,
    fileRefs,
    isSigned,
    lookupDocument,
    repositoryId,
    setUploadFiles,
    successfulOrdered,
  } = params;

  const newDocumentEntries: RepositoryDropzoneSuccessfulStackEntry[] = [];
  const versionEntries: VersionPersistEntry[] = [];

  for (const entry of successfulOrdered) {
    const localFile = fileRefs.get(entry.id);
    const bytes = localFile ? await localFile.arrayBuffer() : undefined;
    const target = await resolveDocxUploadTarget({
      bytes,
      filename: localFile?.name ?? entry.result.filename,
      lookupDocument,
      mimetype: entry.result.mimetype || localFile?.type,
      url: entry.result.url,
    });

    if (target.kind === 'versionCandidate') {
      const choice = await confirmUploadAsLatestVersion(target.document.name);
      if (choice === 'asVersion') {
        versionEntries.push({ documentId: target.document.id, entry });
        continue;
      }
    }

    newDocumentEntries.push(entry);
  }

  await persistVersionUploads({
    createStaticFileVersions,
    setUploadFiles,
    versionEntries,
  });

  if (!newDocumentEntries.length) return;

  await persistNewDocumentUploads({
    createFiles,
    documentVariant,
    isSigned,
    repositoryId,
    setUploadFiles,
    successfulOrdered: newDocumentEntries,
  });
}

async function persistVersionUploads(options: {
  createStaticFileVersions: RepositoryDropzoneCreateStaticFileVersions;
  setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>;
  versionEntries: VersionPersistEntry[];
}): Promise<void> {
  const { createStaticFileVersions, setUploadFiles, versionEntries } = options;
  if (!versionEntries.length) return;

  let data: Apollo.CreateStaticFileVersionsMutation | null | undefined;
  let errors: readonly GraphQLFormattedError[] | undefined;

  try {
    ({ data, errors } = await createStaticFileVersions({
      variables: {
        input: {
          records: versionEntries.map(({ documentId, entry }) => ({
            documentId,
            props: { fileLocation: toFileLocationProps(entry.result) },
          })),
        },
      },
    }));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
    markUploadsFailed(
      setUploadFiles,
      versionEntries.map(({ entry }) => entry.id)
    );
    return;
  }

  const firstError = errors?.[0];
  if (firstError !== undefined) {
    Sentry.captureException(firstError, {
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
    markUploadsFailed(
      setUploadFiles,
      versionEntries.map(({ entry }) => entry.id)
    );
    return;
  }

  const created = data?.createStaticFileVersions ?? [];
  if (created.length !== versionEntries.length) {
    Sentry.captureException(new Error('Version count mismatch in createStaticFileVersions'), {
      extra: {
        created: created.map(v => v?.id),
        received: created.length,
        sent: versionEntries.length,
        versionEntries: versionEntries.map(({ entry }) => entry.id),
      },
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
  }

  const successMap = new Map(versionEntries.map(({ entry }, i) => [entry.id, i]));
  setUploadFiles(prev =>
    prev.map(f => {
      const idx = successMap.get(f.id);
      if (idx === undefined) return f;
      const version = created[idx];
      const documentId = versionEntries[idx]?.documentId;

      if (!version || !documentId) {
        Sentry.captureException(new Error('Missing file version at expected index'), {
          extra: {
            createdLength: created.length,
            expectedIndex: idx,
            fileId: f.id,
            filename: f.filename,
          },
          tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
        });
      }

      return version && documentId
        ? { ...f, documentId, progress: 100, state: 'completed' }
        : {
            ...f,
            error: 'Failed to save documents. Please try again or contact support.',
            state: 'failed',
          };
    })
  );
}

async function persistNewDocumentUploads(options: {
  createFiles: RepositoryDropzoneCreateFiles;
  documentVariant: Apollo.DocumentVariant;
  isSigned: boolean;
  repositoryId: UUID;
  setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>;
  successfulOrdered: RepositoryDropzoneSuccessfulStackEntry[];
}): Promise<void> {
  const {
    createFiles,
    documentVariant,
    isSigned,
    repositoryId,
    setUploadFiles,
    successfulOrdered,
  } = options;

  let data: Apollo.CreateFilesMutation | null | undefined;
  let errors: readonly GraphQLFormattedError[] | undefined;

  try {
    ({ data, errors } = await createFiles({
      reportId: repositoryId,
      variables: {
        input: {
          records: successfulOrdered.map(({ result }) => ({
            documentProps: {
              isSigned,
              variant: documentVariant,
            },
            fileLocationProps: toFileLocationProps(result),
          })),
        },
      },
    }));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
    markUploadsFailed(
      setUploadFiles,
      successfulOrdered.map(s => s.id)
    );
    return;
  }

  const firstError = errors?.[0];
  if (firstError !== undefined) {
    Sentry.captureException(firstError, {
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
    markUploadsFailed(
      setUploadFiles,
      successfulOrdered.map(s => s.id)
    );
    return;
  }

  const created = data?.createFiles ?? [];

  if (created.length !== successfulOrdered.length) {
    Sentry.captureException(new Error('Document count mismatch in createFiles mutation'), {
      extra: {
        created: created.map(d => d?.id),
        received: created.length,
        sent: successfulOrdered.length,
        successfulOrdered: successfulOrdered.map(s => s.id),
      },
      tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
    });
  }

  const successMap = new Map(successfulOrdered.map((s, i) => [s.id, i]));
  setUploadFiles(prev =>
    prev.map(f => {
      const idx = successMap.get(f.id);
      if (idx === undefined) return f;
      const doc = created[idx];

      if (!doc) {
        Sentry.captureException(new Error('Missing document at expected index'), {
          extra: {
            createdLength: created.length,
            expectedIndex: idx,
            fileId: f.id,
            filename: f.filename,
          },
          tags: { feature: REPOSITORY_DROPZONE_SENTRY_FEATURE },
        });
      }

      return doc
        ? { ...f, documentId: doc.id, progress: 100, state: 'completed' }
        : {
            ...f,
            error: 'Document was not created. Please try again or contact support.',
            state: 'failed',
          };
    })
  );
}

function toFileLocationProps(result: FilestackUploadPayload) {
  return {
    container: result.container,
    filename: extractFileName(result.filename),
    handle: result.handle,
    key: result.key,
    mimetype: result.mimetype,
    size: result.size,
    url: result.url,
  };
}

function markUploadsFailed(
  setUploadFiles: Dispatch<SetStateAction<UploadFile[]>>,
  fileIds: string[]
): void {
  const failedIds = new Set(fileIds);
  setUploadFiles(prev =>
    prev.map(f =>
      failedIds.has(f.id)
        ? {
            ...f,
            error: 'Failed to save documents. Please try again or contact support.',
            state: 'failed',
          }
        : f
    )
  );
}
